import React from 'react';
import { BrowserRouter as Router, Switch, Route, Redirect } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './stores/authStore';

import Navigation from './components/Navigation';
import AdminLayout from './components/AdminLayout';
import Home from './pages/Home';
import DramaDetail from './pages/DramaDetail';
import Search from './pages/Search';
import Profile from './pages/Profile';
import Login from './pages/Login';
import Register from './pages/Register';
import MongoLogin from './pages/MongoLogin';        // 新增：MongoDB 登录
import MongoRegister from './pages/MongoRegister';  // 新增：MongoDB 注册
import MongoProfile from './pages/MongoProfile';    // 新增：MongoDB 个人主页
import SystemStatus from './pages/SystemStatus';    // 新增：系统状态
import Settings from './pages/Settings';
import Admin from './pages/Admin';
// 彩票页面
import LotteryPickPage from './pages/LotteryPickPage';
import LotteryListPage from './pages/LotteryListPage';
import LotteryRedeemPage from './pages/LotteryRedeemPage';
// === 真实彩票模块（新增独立模块，零侵入） ===
import RealLotteryHome from './pages/real-lottery/RealLotteryHome';
import RealLotteryPickPage from './pages/real-lottery/RealLotteryPickPage';
import RealLotteryOrdersPage from './pages/real-lottery/RealLotteryOrdersPage';
import RealLotteryOrderDetailPage from './pages/real-lottery/RealLotteryOrderDetailPage';
import RealLotteryRedeemPage from './pages/real-lottery/RealLotteryRedeemPage';
import RealLotteryRedemptionsPage from './pages/real-lottery/RealLotteryRedemptionsPage';
import DramaEdit from './pages/DramaEdit';
import AdminDashboard from './pages/AdminDashboard';
import AdminDramas from './pages/AdminDramas';
import AdminUsers from './pages/AdminUsers';
import AdminComments from './pages/AdminComments';
import AdminConfig from './pages/AdminConfig';
import AdminAdvertisements from './pages/AdminAdvertisements';
import DramaManagementPageV2 from './pages/DramaManagementPageV2';
import AdminDramaGenerator from './pages/AdminDramaGenerator';
import AdminPosterGenerator from './pages/AdminPosterGenerator';
import AdminMongoUsers from './pages/AdminMongoUsers';
import DevelopmentPlaceholder from './components/DevelopmentPlaceholder';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#6366f1', // 紫蓝色 - 现代感配色
      light: '#818cf8',
      dark: '#4f46e5',
    },
    secondary: {
      main: '#8b5cf6', // 紫色 - 辅助配色
      light: '#a78bfa',
      dark: '#7c3aed',
    },
    success: {
      main: '#10b981', // 翡翠绿 - 成功状态
    },
    info: {
      main: '#06b6d4', // 青色 - 信息状态
    },
    warning: {
      main: '#f59e0b', // 琥珀色 - 警告状态
    },
    error: {
      main: '#ef4444', // 红色 - 错误状态
    },
    background: {
      default: '#f8fafc', // 浅蓝灰背景
      paper: '#ffffff',
    },
    text: {
      primary: '#0f172a', // 深蓝灰文字
      secondary: '#64748b', // 浅灰文字
    },
  },
  typography: {
    fontFamily: 'Inter, Roboto, sans-serif',
    h1: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 600,
    },
    body1: {
      lineHeight: 1.6,
    },
  },
  shape: {
    borderRadius: 12, // 更圆润的边角
  },
  shadows: [
    'none',
    '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  ],
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Switch>
            {/* 后台管理路由 */}
            <Route path="/admin" render={(props) => (
              <AdminLayout>
                <Switch>
                  {/* 系统数据作为默认首页 */}
                  <Route exact path="/admin" component={AdminDashboard} />
                  <Route path="/admin/dashboard" component={AdminDashboard} />
                  <Route path="/admin/dramas" component={AdminDramas} />
                  <Route path="/admin/drama-management" component={DramaManagementPageV2} />
                  <Route path="/admin/drama-generator" component={AdminDramaGenerator} />
                  <Route path="/admin/poster-generator" component={AdminPosterGenerator} />
                  <Route path="/admin/users" component={AdminUsers} />
                  <Route path="/admin/comments" component={AdminComments} />
                  <Route path="/admin/config" component={AdminConfig} />
                  <Route path="/admin/advertisements" component={AdminAdvertisements} />
                  <Route path="/admin/drama/edit/:id" component={DramaEdit} />
                  <Route path="/admin/drama/new" component={DramaEdit} />
                  {/* MongoDB 管理页面 */}
                  <Route path="/admin/mongo-users" component={AdminMongoUsers} />
                  {/* 新增管理页面 - 开发中 */}
                  <Route 
                    path="/admin/lottery-management" 
                    component={() => <DevelopmentPlaceholder title="彩票管理" />} 
                  />
                  <Route 
                    path="/admin/traffic-management" 
                    component={() => <DevelopmentPlaceholder title="引流管理" />} 
                  />
                  <Route 
                    path="/admin/traffic-statistics" 
                    component={() => <DevelopmentPlaceholder title="流量统计" />} 
                  />
                </Switch>
              </AdminLayout>
            )} />

            {/* 彩票页面 - 放在外层避免嵌套路由问题 */}
            <Route path="/lottery/pick" component={LotteryPickPage} />
            <Route path="/lottery/list" component={LotteryListPage} />
            <Route path="/lottery/redeem" component={LotteryRedeemPage} />

            {/* === 真实彩票模块路由（独立，零侵入） === */}
            <Route exact path="/real-lottery" component={RealLotteryHome} />
            <Route exact path="/real-lottery/pick" component={RealLotteryPickPage} />
            <Route exact path="/real-lottery/orders" component={RealLotteryOrdersPage} />
            <Route exact path="/real-lottery/order/:outTradeNo" component={RealLotteryOrderDetailPage} />
            <Route exact path="/real-lottery/redeem/:outTradeNo" component={RealLotteryRedeemPage} />
            <Route exact path="/real-lottery/redemptions" component={RealLotteryRedemptionsPage} />

            {/* 前台路由 */}
            <Route path="/" render={(props) => (
              <React.Fragment>
                <Navigation />
                <Switch>
                  <Route exact path="/" component={Home} />
                  <Route path="/drama/:id" component={DramaDetail} />
                  <Route path="/search" component={Search} />
                  {/* v2 路由放在前面，避免被前缀匹配 */}
                  <Route path="/login-v2" component={MongoLogin} />
                  <Route path="/register-v2" component={MongoRegister} />
                  <Route path="/profile-v2" component={MongoProfile} />
                  <Route path="/system-status" component={SystemStatus} />
                  {/* 旧系统路由 */}
                  <Route path="/profile" component={Profile} />
                  <Route path="/login" component={Login} />
                  <Route path="/register" component={Register} />
                  <Route path="/settings" component={Settings} />
                  {/* 添加对 /dramas 路径的支持，重定向到 / */}
                  <Route path="/dramas" render={() => <Redirect to="/" />} />
                  <Route path="*" render={() => <Redirect to="/" />} />
                </Switch>
              </React.Fragment>
            )} />
          </Switch>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
